import { FrameInstrumentation, FrameWatchdog } from "./FrameInstrumentation";
import { BoundedQueue } from "./BoundedQueue";
import { GpuUploadThrottle } from "./GpuUploadThrottle";
export const HighPerformanceRuntime = {
    create(_options = {}) {
        var _a, _b, _c, _d;
        const options = _options !== null && _options !== void 0 ? _options : {};
        const instrumentation = options.enableInstrumentation
            ? new FrameInstrumentation()
            : null;
        const watchdog = options.enableInstrumentation ? new FrameWatchdog() : null;
        const worldOpQueue = new BoundedQueue({
            maxSize: (_a = options.worldOpQueueSize) !== null && _a !== void 0 ? _a : 8,
            dropPolicy: "drop-oldest",
        });
        const systemApiQueue = new BoundedQueue({
            maxSize: (_b = options.systemApiQueueSize) !== null && _b !== void 0 ? _b : 6,
            dropPolicy: "drop-newest",
        });
        const gpuUploadThrottle = new GpuUploadThrottle({
            maxUploadsPerSecond: (_c = options.gpuUploadMaxPerSecond) !== null && _c !== void 0 ? _c : 16,
            maxQueueSize: (_d = options.gpuUploadQueueSize) !== null && _d !== void 0 ? _d : 200,
            dropPolicy: "drop-oldest",
        });
        let activeWorldOp = null;
        let activeSystemApiOp = null;
        let lastFrameTime = 0;
        const startNextWorldOp = () => {
            if (activeWorldOp)
                return;
            const next = worldOpQueue.dequeue();
            if (!next)
                return;
            try {
                const result = next.run();
                if (result && typeof result.then === "function") {
                    activeWorldOp = result
                        .catch((err) => {
                        console.warn("[HighPerformanceRuntime] World op failed", {
                            label: next.label,
                            err,
                        });
                    })
                        .finally(() => {
                        activeWorldOp = null;
                    });
                }
            }
            catch (err) {
                console.warn("[HighPerformanceRuntime] World op failed", {
                    label: next.label,
                    err,
                });
                activeWorldOp = null;
            }
        };
        const startNextSystemApiOp = () => {
            if (activeSystemApiOp)
                return;
            const next = systemApiQueue.dequeue();
            if (!next)
                return;
            try {
                const result = next.run();
                if (result && typeof result.then === "function") {
                    activeSystemApiOp = result
                        .then((value) => {
                        next.resolve(value);
                    })
                        .catch((err) => {
                        next.reject(err);
                        console.warn("[HighPerformanceRuntime] System API op failed", {
                            label: next.label,
                            err,
                        });
                    })
                        .finally(() => {
                        activeSystemApiOp = null;
                    });
                }
                else {
                    next.resolve(result);
                    activeSystemApiOp = null;
                }
            }
            catch (err) {
                next.reject(err);
                console.warn("[HighPerformanceRuntime] System API op failed", {
                    label: next.label,
                    err,
                });
                activeSystemApiOp = null;
            }
        };
        return {
            start() {
                // Harness initialization placeholder.
                lastFrameTime = 0;
            },
            stop() {
                // Harness teardown placeholder.
                lastFrameTime = 0;
            },
            frame(now) {
                if (lastFrameTime > 0) {
                    const frameMs = now - lastFrameTime;
                    instrumentation === null || instrumentation === void 0 ? void 0 : instrumentation.recordFrame(frameMs);
                    if (instrumentation && watchdog) {
                        watchdog.evaluate(instrumentation.getSnapshot());
                    }
                }
                lastFrameTime = now;
                gpuUploadThrottle.runFrame(now);
                startNextWorldOp();
                startNextSystemApiOp();
            },
            enqueueWorldOp(label, run) {
                return worldOpQueue.enqueue({ label, run });
            },
            enqueueSystemApiOp(label, run) {
                return new Promise((resolve, reject) => {
                    const queued = systemApiQueue.enqueue({
                        label,
                        run,
                        resolve: resolve,
                        reject,
                    });
                    if (!queued) {
                        const err = new Error("System API queue full");
                        reject(err);
                        console.warn("[HighPerformanceRuntime] Dropped system API op", {
                            label,
                        });
                    }
                });
            },
            enqueueGpuUpload(label, run) {
                return gpuUploadThrottle.schedule({
                    label,
                    run,
                });
            },
        };
    },
};
export { BoundedQueue, } from "./BoundedQueue";
export { CoalescingQueue, } from "./CoalescingQueue";
export { FrameBudgetScheduler, } from "./FrameBudgetScheduler";
export { FrameInstrumentation, FrameWatchdog, } from "./FrameInstrumentation";
export { GpuUploadThrottle, } from "./GpuUploadThrottle";
export { SpawnBatch, SpawnThrottle, } from "./SpawnThrottle";
export { probeRenderWorkerSupport, startRenderWorkerSession, } from "./RenderWorkerBridge";
export { createTypedUpdateBatch, isTypedUpdateBatch, validateTypedUpdateBatch, } from "./TypedUpdateBatch";
